import * as yup from 'yup'
import { messages } from 'helpers/validationMessages'
import { EMAIL_REGEX } from 'constants/validation'

/**
 * Схемы валидации для форм компании
 * Основаны на требованиях из openapi.yaml
 */

// ИНН: 10 цифр (ЮЛ) или 12 цифр (ИП)
export const innSchema = yup
  .string()
  .required('ИНН обязателен для заполнения')
  .matches(/^\d{10}$|^\d{12}$/, 'ИНН должен содержать 10 или 12 цифр')

// ИНН с валидацией, зависящей от clientSubtype в той же схеме
const innConditionalSchema = yup
  .string()
  .required('ИНН обязателен для заполнения')
  .when('clientSubtype', {
    is: 'llc',
    then: schema => schema.matches(/^\d{10}$/, 'ИНН должен содержать 10 цифр'),
    otherwise: schema =>
      schema.matches(/^\d{12}$/, 'ИНН должен содержать 12 цифр'),
  })

// КПП: 9 цифр (не обязателен для ИП)
export const kppSchema = yup
  .string()
  .nullable()
  .test('kpp-format', 'КПП должен содержать 9 цифр', value => {
    if (!value) return true // Необязательное поле
    return /^\d{9}$/.test(value)
  })

// ОГРН: 13 цифр (ЮЛ) или 15 цифр (ОГРНИП для ИП)
export const ogrnSchema = yup
  .string()
  .required('ОГРН/ОГРНИП обязателен для заполнения')
  .matches(/^\d{13}$|^\d{15}$/, 'ОГРН должен содержать 13 или 15 цифр')

// БИК: 9 цифр
export const bikSchema = yup
  .string()
  .required('БИК обязателен для заполнения')
  .matches(/^\d{9}$/, 'БИК должен содержать 9 цифр')

// Расчетный счет: 20 цифр
export const accountSchema = yup
  .string()
  .required('Расчетный счет обязателен для заполнения')
  .matches(/^\d{20}$/, 'Расчетный счет должен содержать 20 цифр')

// Корреспондентский счет: 20 цифр
export const correspondentAccountSchema = yup
  .string()
  .required('Корреспондентский счет обязателен для заполнения')
  .matches(/^\d{20}$/, 'Корреспондентский счет должен содержать 20 цифр')

// Шаг 1: Контактные данные
export const contactDataStepSchema = yup.object().shape({
  email: yup.string().required('Email обязателен для заполнения'),
  //.matches(EMAIL_REGEX, 'Введите корректный email'),
  firstName: yup
    .string()
    .required(messages.required)
    .max(100, messages.maxLength(100))
    .test('notOnlySpaces', messages.required, value => value?.trim() !== ''),
  lastName: yup
    .string()
    .required(messages.required)
    .max(100, messages.maxLength(100))
    .test('notOnlySpaces', messages.required, value => value?.trim() !== ''),
  fatherName: yup
    .string()
    .nullable()
    .max(100, messages.maxLength(100))
    .test(
      'notOnlySpaces',
      messages.required,
      value => !value || value.trim() !== '',
    ),
  customerAddress: yup.object().shape({
    address: yup.string().required('Адрес обязателен для заполнения'),
    fiasId: yup.string().required('Выберите адрес из списка'),
    geoLat: yup.string().required(),
    geoLon: yup.string().required(),
  }),
  agreeProcessingPersonalData: yup
    .boolean()
    .test(
      'personal-agree',
      'Необходимо согласие на обработку персональных данных',
      value => value === true,
    ),
  agreeSubscribeAdvertising: yup.boolean(),
})

// Шаг 2: Данные компании
export const companyDetailsStepSchema = yup.object().shape({
  // Реквизиты компании
  companyName: yup
    .string()
    .required('Название компании обязательно для заполнения')
    .max(128, messages.maxLength(128))
    .test(
      'notOnlySpaces',
      'Название компании обязательно для заполнения',
      value => value?.trim() !== '',
    ),
  inn: innConditionalSchema,
  kpp: kppSchema,
  ogrn: ogrnSchema,

  // Адреса компании
  legalAddress: yup.object().shape({
    address: yup
      .string()
      .required('Юридический адрес обязателен для заполнения'),
    fiasId: yup.string().required('Выберите адрес из списка'),
    geoLat: yup.string().required(),
    geoLon: yup.string().required(),
  }),
  actualAddress: yup
    .object()
    .shape({
      address: yup.string().nullable(),
      fiasId: yup.string().nullable(),
      geoLat: yup.string().nullable(),
      geoLon: yup.string().nullable(),
    })
    .nullable(),

  // Руководитель
  directorFullName: yup
    .string()
    .required('ФИО руководителя обязательно для заполнения')
    .max(100, messages.maxLength(100))
    .test(
      'notOnlySpaces',
      'ФИО руководителя обязательно для заполнения',
      value => value?.trim() !== '',
    ),
  directorPosition: yup
    .string()
    .required('Должность руководителя обязательна для заполнения')
    .max(128, messages.maxLength(128))
    .test(
      'notOnlySpaces',
      'Должность руководителя обязательна для заполнения',
      value => value?.trim() !== '',
    ),

  // Банковские реквизиты
  bankDetails: yup.object().shape({
    bankName: yup
      .string()
      .required('Название банка обязательно для заполнения')
      .max(255, messages.maxLength(128))
      .test(
        'notOnlySpaces',
        'Название банка обязательно для заполнения',
        value => value?.trim() !== '',
      ),
    bik: bikSchema,
    correspondentAccount: correspondentAccountSchema,
    currentAccount: accountSchema,
  }),
})

// Полная схема валидации для всей формы регистрации (оба шага)
export const companyRegistrationSchema = yup.object().shape({
  // Шаг 1: Контактные данные
  email: yup
    .string()
    .required('Email обязателен для заполнения')
    .matches(EMAIL_REGEX, 'Введите корректный email')
    .test(
      'no-com-domain',
      `В связи с новым законодательством, вы не можете зарегистрировать аккаунт на иностранный почтовый адрес
       (заканчивается на ".com"). Пожалуйста, воспользуйтесь другой почтой.`,
      value => {
        if (!value) return true

        return !value.toLowerCase().endsWith('.com')
      },
    ),
  firstName: yup
    .string()
    .required(messages.required)
    .max(100, messages.maxLength(100))
    .test('notOnlySpaces', messages.required, value => value?.trim() !== ''),
  lastName: yup
    .string()
    .required(messages.required)
    .max(100, messages.maxLength(100))
    .test('notOnlySpaces', messages.required, value => value?.trim() !== ''),
  fatherName: yup
    .string()
    .nullable()
    .max(100, messages.maxLength(100))
    .test(
      'notOnlySpaces',
      messages.required,
      value => !value || value.trim() !== '',
    ),
  agreeProcessingPersonalData: yup
    .boolean()
    .test(
      'personal-agree',
      'Необходимо согласие на обработку персональных данных',
      value => value === true,
    ),
  agreeSubscribeAdvertising: yup.boolean(),

  // Шаг 2: Данные компании
  companyName: yup
    .string()
    .required('Название компании обязательно для заполнения')
    .max(128, messages.maxLength(128))
    .test(
      'notOnlySpaces',
      'Название компании обязательно для заполнения',
      value => value?.trim() !== '',
    ),
  inn: innConditionalSchema,
  kpp: kppSchema,
  ogrn: ogrnSchema,
  legalAddress: yup
    .object()
    .shape({
      address: yup
        .string()
        .required('Юридический адрес обязателен для заполнения'),
      fiasId: yup.string().required('Выберите адрес из списка'),
      geoLat: yup.string().required(),
      geoLon: yup.string().required(),
    })
    .nullable()
    .required('Юридический адрес обязателен для заполнения'),
  actualAddress: yup
    .object()
    .shape({
      address: yup.string().nullable(),
      fiasId: yup.string().nullable(),
      geoLat: yup.string().nullable(),
      geoLon: yup.string().nullable(),
    })
    .nullable(),
  directorFullName: yup
    .string()
    .required('ФИО руководителя обязательно для заполнения')
    .max(100, messages.maxLength(100))
    .test(
      'notOnlySpaces',
      'ФИО руководителя обязательно для заполнения',
      value => value?.trim() !== '',
    ),
  directorPosition: yup
    .string()
    .required('Должность руководителя обязательна для заполнения')
    .max(128, messages.maxLength(128))
    .test(
      'notOnlySpaces',
      'Должность руководителя обязательна для заполнения',
      value => value?.trim() !== '',
    ),
  bankDetails: yup.object().shape({
    bankName: yup
      .string()
      .required('Название банка обязательно для заполнения')
      .max(255, messages.maxLength(128))
      .test(
        'notOnlySpaces',
        'Название банка обязательно для заполнения',
        value => value?.trim() !== '',
      ),
    bik: bikSchema,
    correspondentAccount: correspondentAccountSchema,
    currentAccount: accountSchema,
  }),
})

// Схема валидации для формы добавления/редактирования банковских реквизитов
export const bankDetailsSchema = yup.object().shape({
  bankName: yup
    .string()
    .required('Название банка обязательно для заполнения')
    .max(255, messages.maxLength(128))
    .test(
      'notOnlySpaces',
      'Название банка обязательно для заполнения',
      value => value?.trim() !== '',
    ),
  bik: bikSchema,
  correspondentAccount: correspondentAccountSchema,
  currentAccount: accountSchema,
  isDefault: yup.boolean(),
})
